import React, { useState } from "react";
import { CaratClosedIcon } from "../controls/ThermalHeatMapSvgs";
import { HeatmapRow } from "../../hooks/ThermalHeatMapHooks";
import { AlertEvent } from "../../hooks/useThermalAlerts";
import { ThresholdConfig } from "./ThermalAlertThresholds";
import type { ThermalModelConfig } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/ThermalConfig";
import SafeMarginSection from "../margins/SafeMarginSection";
import PerFaceSection from "./PerFaceSection";
import EnvironmentalSection from "./EnvironmentalSection";
import ThermalAlertThresholds from "./ThermalAlertThresholds";
import ThermalAlertsPanel from "./ThermalAlertsPanel";
import type { PanelVisibilitySettings } from "../../hooks/useThermalFilters";

export interface SummaryItem {
  label: string;
  value: string;
  color: string;
  face?: string;
}

export interface FaceSummary {
  face: string;
  label: string;
  max: number;
  min: number;
}

export interface EnvParams {
  albedo_factor: number | null;
  blackbody_temperature: number | null;
  solar_constant: number | null;
}

export interface ThermalHeatMapSummaryProps {
  // View mode flags
  isFullscreen: boolean;
  isCompact: boolean;

  // Summary data
  summaryItems: SummaryItem[];
  summary: FaceSummary[];
  internalSummary: FaceSummary[];
  envParams: EnvParams;

  // Heatmap data
  heatmapRows: HeatmapRow[];

  // Thermal model (for per-face/subsystem threshold resolution)
  thermalModel: ThermalModelConfig | null;

  // Alerts and thresholds
  alerts: AlertEvent[];
  isAlertsLoading: boolean;
  thresholds: ThresholdConfig[];
  activeThresholds: ThresholdConfig[] | null;
  onThresholdsChange: (updated: ThresholdConfig[]) => void;
  onEvaluateThresholds: (updated: ThresholdConfig[]) => void;
  onAlertClick: (alert: AlertEvent) => void;

  // Global panel show/hide state from the Sections filter category
  panelVisibility: PanelVisibilitySettings;
}

const OverviewSection: React.FC<{ summaryItems: SummaryItem[] }> = React.memo(({ summaryItems }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="thermal-heatmap-summary-box">
      <div className="thermal-heatmap-summary-header-row" onClick={() => setIsOpen((v) => !v)}>
        <button className="thm-summary-toggle-btn">
          <span
            style={{
              display: "flex",
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 300ms ease",
            }}
          >
            <CaratClosedIcon active={isOpen} />
          </span>
        </button>
        <div className="thermal-heatmap-summary-header">OVERVIEW</div>
      </div>
      {isOpen && (
        <div className="thermal-heatmap-summary-items-box">
          {summaryItems.map((item, index) => (
            <div key={index} className="thermal-heatmap-summary-item">
              <span className="thermal-heatmap-summary-item-label">{item.label}</span>
              <div className="thermal-heatmap-summary-item-value-face">
                <span className="thermal-heatmap-summary-item-value" style={{ color: item.color }}>
                  {item.value}
                </span>
                {item.face && <span className="thermal-heatmap-summary-item-face">{item.face}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

const ThermalHeatMapSummary: React.FC<ThermalHeatMapSummaryProps> = ({
  isFullscreen,
  isCompact,
  summaryItems,
  summary,
  internalSummary,
  envParams,
  heatmapRows,
  thermalModel,
  alerts,
  isAlertsLoading,
  thresholds,
  activeThresholds,
  onThresholdsChange,
  onEvaluateThresholds,
  onAlertClick,
  panelVisibility,
}) => {
  // Don't render anything in compact mode
  if (isCompact) {
    return null;
  }

  if (isFullscreen) {
    return (
      <>
        {/* Row 1: SAFE MARGIN (1/3) | ALERTS (2/3) */}
        <div className="thm-fullscreen-row-split">
          <div className="thm-fullscreen-col-1-3">
            {panelVisibility.showPerFace && (
              <PerFaceSection
                summary={summary}
                internalSummary={internalSummary}
                defaultOpen={true}
              />
            )}
            {panelVisibility.showEnvironmental && (
              <EnvironmentalSection
                envParams={envParams}
                defaultOpen={true}
              />
            )}
          </div>
          <div className="thm-fullscreen-col-2-3">
            {panelVisibility.showAlerts && (
              <ThermalAlertsPanel
                alerts={alerts}
                isLoading={isAlertsLoading}
                defaultOpen={true}
                onAlertClick={onAlertClick}
                heatmapRows={heatmapRows}
                thresholds={thresholds}
              />
            )}
          </div>
        </div>

        {/* Row 2: THRESHOLDS (1/3) | PER FACE (1/3) | ENVIRONMENTAL (1/3)
        <div className="thm-fullscreen-row-thirds">
          <div className="thm-fullscreen-col-1-3">
            <ThermalAlertThresholds
              thresholds={thresholds}
              onChange={onThresholdsChange}
              onApply={onEvaluateThresholds}
              defaultOpen={true}
            />
          </div>
          
        </div> */}
      </>
    );
  }

  return (
    <>
      <OverviewSection summaryItems={summaryItems} />
      {/* <SafeMarginSection
        heatmapRows={heatmapRows}
        stepIndex={stepIndex}
        stepIndexOverride={stepIndexOverride}
        setStepIndexOverride={setStepIndexOverride}
        thermalModel={thermalModel}
      /> */}
      {panelVisibility.showAlerts && (
        <ThermalAlertsPanel
          alerts={alerts}
          isLoading={isAlertsLoading}
          onAlertClick={onAlertClick}
          heatmapRows={heatmapRows}
          thresholds={thresholds}
        />
      )}
      <ThermalAlertThresholds
        thresholds={activeThresholds || thresholds}
        onChange={onThresholdsChange}
        onApply={onEvaluateThresholds}
      />
      {panelVisibility.showPerFace && (
        <PerFaceSection
          summary={summary}
          internalSummary={internalSummary}
        />
      )}
      {panelVisibility.showEnvironmental && (
        <EnvironmentalSection
          envParams={envParams}
        />
      )}
    </>
  );
};

export default React.memo(ThermalHeatMapSummary);
